import React from 'react';

import { Box, Skeleton, Stack, Typography } from '@mui/material';

import { DEFAULT_PAGE_WIDTH } from './PdfViewer';
import { onboardColors } from '../../theme/onboardTokens';

// Line widths (as % of the text column, unique within a paragraph) so the
// placeholder reads as a letter rather than a uniform grey block.
const PARAGRAPHS = [
  { id: 'opening', lines: [100, 96, 98, 72] },
  { id: 'terms', lines: [100, 94, 99, 97, 58] },
  { id: 'closing', lines: [100, 92, 64] }
];

/**
 * Loading placeholder shaped like an A4 page inside PdfViewer's frame, so the
 * preview keeps its size and position when the rendered PDF replaces it.
 */
const PdfPageSkeleton = ({
  label,
  width = DEFAULT_PAGE_WIDTH
}: {
  label?: string;
  width?: number;
}) => (
  <Box
    aria-busy="true"
    data-testid="pdf-page-skeleton"
    role="status"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: onboardColors.surface.fill,
      padding: '16px',
      width: '100%',
      boxSizing: 'border-box'
    }}
  >
    {label ? (
      <Typography sx={{ color: onboardColors.neutral.textBody, fontSize: '13px' }}>
        {label}
      </Typography>
    ) : null}
    <Stack
      aria-hidden="true"
      gap="28px"
      sx={{
        width: '100%',
        maxWidth: width,
        aspectRatio: '210 / 297',
        boxSizing: 'border-box',
        padding: '9% 10%',
        backgroundColor: onboardColors.surface.white,
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden'
      }}
    >
      <Box>
        <Skeleton animation="wave" height={28} variant="text" width="45%" />
        <Skeleton animation="wave" height={18} variant="text" width="25%" />
      </Box>
      {PARAGRAPHS.map(({ id, lines }) => (
        <Box key={id}>
          {lines.map((lineWidth) => (
            <Skeleton
              key={`${id}-${lineWidth}`}
              animation="wave"
              height={16}
              variant="text"
              width={`${lineWidth}%`}
            />
          ))}
        </Box>
      ))}
      <Box sx={{ marginTop: 'auto' }}>
        <Skeleton animation="wave" height={48} variant="rounded" width="35%" />
        <Skeleton animation="wave" height={16} sx={{ mt: 1 }} variant="text" width="30%" />
      </Box>
    </Stack>
  </Box>
);

export default PdfPageSkeleton;
